import type { NavigationEntry } from "@positive/ui";

/**
 * What the signed-in user may see (ADR-0003/D-4, D-5). Deny by default: a key that is not in the effective set is
 * not granted. The UI only hides and blocks — the API is the authority and checks the same keys.
 */
export interface Access {
  can: (permission: string) => boolean;
  /** Plan module enabled for the tenant (P6). Everything is enabled in the operator console. */
  hasModule: (module: string) => boolean;
  isOperator: boolean;
  /** Signed in as a tenant member with a loaded session (`/me`). */
  isTenantUser: boolean;
}

export const noAccess: Access = {
  can: () => false,
  hasModule: () => false,
  isOperator: false,
  isTenantUser: false,
};

/** Modules that are always on (module map: platform core and master data). */
const alwaysOnModules = new Set(["platform", "md"]);

export function createAccess({
  permissions,
  modules,
  isOperator = false,
  isTenantUser = true,
}: {
  permissions: readonly string[];
  modules: readonly string[];
  isOperator?: boolean;
  isTenantUser?: boolean;
}): Access {
  const granted = new Set(permissions);
  const enabled = new Set(modules);
  return {
    can: (permission) => granted.has(permission),
    hasModule: (module) => alwaysOnModules.has(module) || enabled.has(module),
    isOperator,
    isTenantUser,
  };
}

/** Who may open a route or see a navigation entry. Every route declares one; a route without it is denied. */
export type RouteAccess = { public: true } | { permission: string; module?: string } | { operator: true };

export function isAccessAllowed(rule: RouteAccess | undefined, access: Access): boolean {
  if (!rule) return false;
  if ("public" in rule) return access.isTenantUser || access.isOperator;
  if ("operator" in rule) return access.isOperator;
  return (
    access.isTenantUser && access.can(rule.permission) && (rule.module === undefined || access.hasModule(rule.module))
  );
}

/**
 * Why a route is closed: its plan module is not in the tenant's subscription (only the platform operator can add it,
 * SRV-J-02), or a permission is missing. A missing module wins: no permission opens it.
 */
export type AccessDenial = { reason: "module"; module: string } | { reason: "permission" };

export function accessDenial(rule: RouteAccess | undefined, access: Access): AccessDenial | null {
  if (isAccessAllowed(rule, access)) return null;
  if (
    rule &&
    "permission" in rule &&
    rule.module !== undefined &&
    access.isTenantUser &&
    !access.hasModule(rule.module)
  )
    return { reason: "module", module: rule.module };
  return { reason: "permission" };
}

function entryRule(entry: NavigationEntry): RouteAccess | undefined {
  if (entry.operatorOnly) return { operator: true };
  if (entry.public) return { public: true };
  if (entry.permission) return { permission: entry.permission, ...(entry.module ? { module: entry.module } : {}) };
  return undefined;
}

/**
 * Navigation filtered by permissions (ADR-0010/D-2): a leaf is shown only when its rule allows it (deny by default);
 * a group is shown when its module is enabled and at least one child remains.
 */
export function filterNavigation(entries: readonly NavigationEntry[], access: Access): NavigationEntry[] {
  return entries.flatMap((entry): NavigationEntry[] => {
    if (entry.children) {
      if (entry.module && !access.hasModule(entry.module)) return [];
      const children = filterNavigation(entry.children, access);
      return children.length > 0 ? [{ ...entry, children }] : [];
    }
    return isAccessAllowed(entryRule(entry), access) ? [entry] : [];
  });
}
